import React from 'react';
import { CheckCircle2, Target, Zap, Shield, Award, Users, Building2, Clock } from 'lucide-react';

const AboutSection = () => {
  const features = [
    { icon: Target, text: 'Sub-millimetre accuracy on every point cloud survey and Matterport scan' },
    { icon: Zap, text: 'Latest Matterport Pro3 cameras and high-specification 3D laser scanners' },
    { icon: Shield, text: 'Trusted by NHS Trusts, Tier 1 contractors and FTSE 100 companies' },
    { icon: Award, text: 'Certified Matterport Service Partner with 1,000+ scans completed' },
    { icon: Users, text: 'Dedicated project managers for every engagement, large or small' },
    { icon: Clock, text: 'Fast turnaround: scan data delivered within 48 hours as standard' },
  ];

  const stats = [
    { icon: Building2, value: '1,000+', label: 'Scans Completed' },
    { icon: Users, value: '200+', label: 'Clients Served' },
    { icon: Clock, value: '7+', label: 'Years Experience' },
    { icon: Target, value: '99.9%', label: 'Accuracy Rate' },
  ];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left: Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full mb-6">
                <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                <span className="text-blue-600 text-sm font-semibold">ABOUT DIGITAL SCANNING GROUP</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Digital Reality Capture for{' '}
                <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                  Buildings That Matter
                </span>
              </h2>
            </div>

            <div className="space-y-6 text-lg text-gray-600 leading-relaxed">
              <p>
                When projects are complex, environments are live, and decisions carry risk, assumptions are not enough.
                Digital Scanning Group was founded with a clear mission: to bring precision, clarity and confidence to
                the built environment through cutting-edge 3D scanning and digital twin technology. As a leading provider
                of <strong className="text-gray-900">Matterport 3D scanning in London</strong> and across the UK, we have
                spent over seven years refining our craft and building long-term relationships with some of the most
                demanding organisations in construction, healthcare, heritage and commercial property.
              </p>
              <p>
                Digital Scanning Group delivers <strong className="text-gray-900">accurate, visual and defensible digital records of buildings and spaces</strong>,
                using advanced Matterport 3D scanning, point cloud surveys, Revit BIM modelling, drone surveys,
                timelapse cameras and CGI visualisation. Our team of experienced surveyors, BIM technicians and
                project managers brings deep technical knowledge paired with a consultative approach. We do not
                simply capture data — we work alongside your team to understand what you need from that data, and
                we deliver it in formats that integrate seamlessly into your existing workflows.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 pt-4">Matterport 3D Scanning Across London and the UK</h3>
              <p>
                Our <strong className="text-gray-900">Matterport London</strong> service is the cornerstone of what we do.
                Using the latest Matterport Pro3 cameras, we create immersive, photorealistic 3D virtual tours and
                accurate digital twins for commercial offices, residential developments, healthcare facilities,
                construction sites and heritage buildings. As a certified Matterport service partner, we bring
                unmatched expertise in capturing spaces of every size and complexity. From a single consultation room
                to an entire hospital campus, our <strong className="text-gray-900">3D scanning London</strong> team
                delivers consistent, high-quality results that clients rely on for critical decision-making. We are
                proud to be recognised as one of the most experienced <strong className="text-gray-900">Matterport UK</strong> providers,
                with over 1,000 scans completed nationwide.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 pt-4">Point Cloud Surveys and Laser Scanning</h3>
              <p>
                Our <strong className="text-gray-900">point cloud surveys</strong> deliver sub-millimetre accuracy for
                as-built documentation, renovation planning, heritage preservation and construction verification.
                Using high-specification 3D laser scanners alongside our Matterport equipment, we produce dense,
                registered point cloud datasets that architects, engineers and contractors depend on for design
                coordination, clash detection and accurate measurement. <strong className="text-gray-900">Point cloud
                survey London</strong> projects form a significant part of our workload, with regular commissions from
                architectural practices, MEP consultancies and main contractors working on refurbishment and new-build
                schemes across the capital. We deliver data in all standard formats including RCP, E57, LAS and XYZ,
                ensuring compatibility with your preferred software platforms.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 pt-4">Scan to BIM and Revit Modelling</h3>
              <p>
                Digital Scanning Group offers comprehensive <strong className="text-gray-900">scan to BIM</strong> services,
                converting raw point cloud data into fully coordinated Revit BIM models. Our experienced Revit modelling
                team produces LOD 200 through to LOD 500 models covering architectural
                elements, structural components, MEP systems, gridlines, datums and detailed floor plans. <strong className="text-gray-900">Scan
                to BIM London</strong> is increasingly essential for refurbishment projects, change of use applications, facilities
                management handovers and regulatory compliance. We work closely with architects, contractors and FM teams
                to ensure every model meets the specific requirements of your project and integrates with your existing
                BIM workflows.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 pt-4">London Coverage and UK-Wide Service</h3>
              <p>
                Based in central London at Great Portland Street, Digital Scanning Group provides <strong className="text-gray-900">3D
                scanning services across every London borough</strong> and the wider UK. We regularly undertake projects in the
                City of London, Westminster, Canary Wharf, Kensington, Chelsea, Shoreditch, Camden, Islington, Hackney,
                Southwark, Lambeth, Greenwich and all surrounding areas. Beyond the capital, our teams travel to sites
                across the South East, Home Counties, Midlands, North of England, Scotland, Wales and Northern Ireland.
                We also serve clients across Europe and internationally, having completed projects in France, Germany,
                Spain, the Netherlands and the Middle East. Wherever your project is located, Digital Scanning Group
                brings the same level of professionalism, accuracy and reliability.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 pt-4">Key Sectors: Construction, NHS and Commercial Property</h3>
              <p>
                We work with <strong className="text-gray-900">construction teams, NHS healthcare trusts, commercial fit-out specialists,
                hospitality groups and heritage stakeholders</strong> to capture reality once and use it intelligently
                throughout the lifecycle of a building. In <strong className="text-gray-900">construction</strong>, our scanning
                services support every phase from pre-works condition surveys and progress monitoring to as-built documentation
                and defect recording. Tier 1 contractors rely on our Matterport scans and point cloud data for transparent
                progress reporting, remote site access and dispute resolution.
              </p>
              <p>
                In the <strong className="text-gray-900">NHS and healthcare</strong> sector, our Matterport virtual tours
                have transformed the patient experience at multiple trusts. Neonatal units use our 3D tours to help anxious
                parents familiarise themselves with clinical environments before their first visit, while estates teams use
                our scanning data for space planning, compliance documentation and maintenance scheduling. For <strong className="text-gray-900">commercial
                property</strong>, we deliver virtual tours that help agents market office spaces, retail units and industrial
                premises to prospective tenants, reducing vacant periods and accelerating leasing decisions.
              </p>
              <p>
                With over 1,000 scans completed, 200+ satisfied clients and projects spanning hospitals, office
                towers, hotel refurbishments, listed heritage buildings and major infrastructure schemes, Digital
                Scanning Group has earned its reputation as a trusted partner for reality capture. Our clients choose
                us because we combine technical excellence with reliability, fast turnaround times and a genuine
                understanding of the sectors we serve. We are proud to count NHS Trusts, Tier 1 contractors, FTSE 100
                companies and award-winning architectural practices among our client base.
              </p>
              <p>
                Whether you need a single room scanned or an entire hospital campus documented in 3D, Digital Scanning
                Group has the expertise, equipment and project management capability to deliver. We invest continuously
                in the latest scanning hardware and software to ensure our clients always benefit from the most accurate
                and efficient capture technology available. Our commitment is simple: deliver data you can trust,
                on time and within budget, every time.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4 pt-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="flex items-center gap-4 group">
                    <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <span className="text-gray-700 font-medium">{feature.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="pt-6">
              <a
                href="#contact"
                className="inline-block px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-full hover:shadow-xl hover:shadow-blue-500/50 transition-all duration-300 hover:scale-105"
                aria-label="Learn more about Digital Scanning Group services"
              >
                Learn More About Us
              </a>
            </div>
          </div>

          {/* Right: Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-6">
                <div className="relative h-64 rounded-2xl overflow-hidden shadow-xl group">
                  <img
                    src="/images/commercial-building-480.webp"
              srcSet="/images/commercial-building-480.webp 480w, /images/commercial-building-800.webp 800w, /images/commercial-building-1200.webp 1200w"
              sizes="(min-width: 1024px) 300px, 45vw"
              width="480"
              height="320"
              loading="lazy"
              decoding="async"
                    alt="Modern commercial building architecture captured using 3D laser scanning technology by Digital Scanning Group"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl group">
                  <img
                    src="/images/scanning-equipment-point-cloud-480.webp"
              srcSet="/images/scanning-equipment-point-cloud-480.webp 480w, /images/scanning-equipment-point-cloud-800.webp 800w, /images/scanning-equipment-point-cloud-1200.webp 1200w"
              sizes="(min-width: 1024px) 300px, 45vw"
              width="480"
              height="639"
              loading="lazy"
              decoding="async"
                    alt="3D scanning equipment and point cloud technology used for building surveys"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
              </div>
              <div className="space-y-6 pt-12">
                <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl group">
                  <img
                    src="/images/digital-twin-hologram-480.webp"
              srcSet="/images/digital-twin-hologram-480.webp 480w, /images/digital-twin-hologram-800.webp 800w, /images/digital-twin-hologram-1200.webp 1200w"
              sizes="(min-width: 1024px) 300px, 45vw"
              width="480"
              height="270"
              loading="lazy"
              decoding="async"
                    alt="Digital twin hologram visualisation of a building created from Matterport 3D scan data"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="relative h-64 rounded-2xl overflow-hidden shadow-xl group">
                  <img
                    src="/images/bim-architectural-detail-480.webp"
              srcSet="/images/bim-architectural-detail-480.webp 480w, /images/bim-architectural-detail-800.webp 800w, /images/bim-architectural-detail-1200.webp 1200w"
              sizes="(min-width: 1024px) 300px, 45vw"
              width="480"
              height="320"
              loading="lazy"
              decoding="async"
                    alt="Architectural detail showing BIM modelling precision for construction project documentation"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
              </div>
            </div>
            {/* Decorative Element */}
            <div className="absolute -top-6 -right-6 w-72 h-72 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl -z-10"></div>
          </div>
        </div>

        {/* Trust Stats Section */}
        <div className="mt-24 pt-16 border-t border-gray-100">
          <div className="text-center mb-12">
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Clients Choose Digital Scanning Group
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From NHS hospitals to FTSE 100 headquarters, our clients trust us to deliver accurate, reliable
              3D scanning and digital twin solutions that drive better decisions and reduce project risk.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div key={index} className="text-center p-6 bg-gray-50 rounded-2xl hover:bg-blue-50 transition-colors duration-300">
                  <div className="inline-flex p-3 bg-blue-100 rounded-xl mb-4">
                    <Icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Sectors Served */}
        <div className="mt-16 p-8 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Sectors We Serve</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'Construction & Infrastructure',
              'NHS & Healthcare',
              'Commercial Property',
              'Heritage & Conservation',
              'Hospitality & Leisure',
              'Education',
              'Retail & Fit-Out',
              'Facilities Management',
            ].map((sector, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <span className="text-gray-700 text-sm font-medium">{sector}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
